// src/config/locationTimezones.js
// Best-guess IANA timezone from a (country, stateProvince) pair, plus a helper to
// format the current local time in that zone. APPROXIMATE: multi-zone countries are
// resolved by state/province where we have it (US/CA/AU are normalized) and fall back
// to a country-level default for single-zone countries. Unknown locations return null.
//
// ⚠ KEEP IN SYNC with functions/locationTimezones.js — a verbatim CommonJS copy
// of STATE_TIMEZONES / COUNTRY_TIMEZONES / resolveTimezone used by the
// trial_scheduled email trigger (Cloud Functions can't import from src/). Any
// edit to the data or the resolve rule here must be mirrored there, or a
// scheduled parent gets emailed the wrong local time.

// State/province -> IANA zone. Where a state spans zones, the most-populous is used.
export const STATE_TIMEZONES = {
  'United States': {
    'Alabama': 'America/Chicago', 'Alaska': 'America/Anchorage', 'Arizona': 'America/Phoenix',
    'Arkansas': 'America/Chicago', 'California': 'America/Los_Angeles', 'Colorado': 'America/Denver',
    'Connecticut': 'America/New_York', 'Delaware': 'America/New_York',
    'District of Columbia': 'America/New_York', 'Florida': 'America/New_York',
    'Georgia': 'America/New_York', 'Hawaii': 'Pacific/Honolulu', 'Idaho': 'America/Denver',
    'Illinois': 'America/Chicago', 'Indiana': 'America/Indiana/Indianapolis',
    'Iowa': 'America/Chicago', 'Kansas': 'America/Chicago', 'Kentucky': 'America/New_York',
    'Louisiana': 'America/Chicago', 'Maine': 'America/New_York', 'Maryland': 'America/New_York',
    'Massachusetts': 'America/New_York', 'Michigan': 'America/Detroit', 'Minnesota': 'America/Chicago',
    'Mississippi': 'America/Chicago', 'Missouri': 'America/Chicago', 'Montana': 'America/Denver',
    'Nebraska': 'America/Chicago', 'Nevada': 'America/Los_Angeles', 'New Hampshire': 'America/New_York',
    'New Jersey': 'America/New_York', 'New Mexico': 'America/Denver', 'New York': 'America/New_York',
    'North Carolina': 'America/New_York', 'North Dakota': 'America/Chicago', 'Ohio': 'America/New_York',
    'Oklahoma': 'America/Chicago', 'Oregon': 'America/Los_Angeles', 'Pennsylvania': 'America/New_York',
    'Rhode Island': 'America/New_York', 'South Carolina': 'America/New_York',
    'South Dakota': 'America/Chicago', 'Tennessee': 'America/Chicago', 'Texas': 'America/Chicago',
    'Utah': 'America/Denver', 'Vermont': 'America/New_York', 'Virginia': 'America/New_York',
    'Washington': 'America/Los_Angeles', 'West Virginia': 'America/New_York',
    'Wisconsin': 'America/Chicago', 'Wyoming': 'America/Denver',
  },
  'Canada': {
    'Alberta': 'America/Edmonton', 'British Columbia': 'America/Vancouver',
    'Manitoba': 'America/Winnipeg', 'New Brunswick': 'America/Halifax',
    'Newfoundland and Labrador': 'America/St_Johns', 'Northwest Territories': 'America/Yellowknife',
    'Nova Scotia': 'America/Halifax', 'Nunavut': 'America/Iqaluit', 'Ontario': 'America/Toronto',
    'Prince Edward Island': 'America/Halifax', 'Quebec': 'America/Toronto',
    'Saskatchewan': 'America/Regina', 'Yukon': 'America/Whitehorse',
  },
  'Australia': {
    'Australian Capital Territory': 'Australia/Sydney', 'New South Wales': 'Australia/Sydney',
    'Northern Territory': 'Australia/Darwin', 'Queensland': 'Australia/Brisbane',
    'South Australia': 'Australia/Adelaide', 'Tasmania': 'Australia/Hobart',
    'Victoria': 'Australia/Melbourne', 'Western Australia': 'Australia/Perth',
  },
};

// Country -> default zone for single-zone countries. Multi-zone countries handled via
// STATE_TIMEZONES are intentionally omitted so they resolve to null without a state.
export const COUNTRY_TIMEZONES = {
  'United Kingdom': 'Europe/London', 'Ireland': 'Europe/Dublin', 'France': 'Europe/Paris',
  'Germany': 'Europe/Berlin', 'Spain': 'Europe/Madrid', 'Italy': 'Europe/Rome',
  'Netherlands': 'Europe/Amsterdam', 'Belgium': 'Europe/Brussels', 'Switzerland': 'Europe/Zurich',
  'Austria': 'Europe/Vienna', 'Sweden': 'Europe/Stockholm', 'Norway': 'Europe/Oslo',
  'Denmark': 'Europe/Copenhagen', 'Finland': 'Europe/Helsinki', 'Poland': 'Europe/Warsaw',
  'Portugal': 'Europe/Lisbon', 'Greece': 'Europe/Athens', 'Czechia': 'Europe/Prague',
  'Romania': 'Europe/Bucharest', 'Hungary': 'Europe/Budapest', 'Turkey': 'Europe/Istanbul',
  'Egypt': 'Africa/Cairo', 'Morocco': 'Africa/Casablanca', 'Algeria': 'Africa/Algiers',
  'Tunisia': 'Africa/Tunis', 'Saudi Arabia': 'Asia/Riyadh', 'United Arab Emirates': 'Asia/Dubai',
  'Qatar': 'Asia/Qatar', 'Kuwait': 'Asia/Kuwait', 'Bahrain': 'Asia/Bahrain', 'Oman': 'Asia/Muscat',
  'Jordan': 'Asia/Amman', 'Lebanon': 'Asia/Beirut', 'Iraq': 'Asia/Baghdad', 'Syria': 'Asia/Damascus',
  'Israel': 'Asia/Jerusalem', 'Palestine': 'Asia/Hebron', 'Yemen': 'Asia/Aden',
  'Pakistan': 'Asia/Karachi', 'India': 'Asia/Kolkata', 'Bangladesh': 'Asia/Dhaka',
  'Sri Lanka': 'Asia/Colombo', 'Singapore': 'Asia/Singapore', 'Malaysia': 'Asia/Kuala_Lumpur',
  'Philippines': 'Asia/Manila', 'Japan': 'Asia/Tokyo', 'South Korea': 'Asia/Seoul',
  'China': 'Asia/Shanghai', 'Thailand': 'Asia/Bangkok', 'Vietnam': 'Asia/Ho_Chi_Minh',
  'New Zealand': 'Pacific/Auckland', 'South Africa': 'Africa/Johannesburg', 'Nigeria': 'Africa/Lagos',
  'Kenya': 'Africa/Nairobi', 'Ghana': 'Africa/Accra', 'Ethiopia': 'Africa/Addis_Ababa',
  'Mexico': 'America/Mexico_City', 'Colombia': 'America/Bogota', 'Peru': 'America/Lima',
  'Argentina': 'America/Argentina/Buenos_Aires', 'Chile': 'America/Santiago',
  'Venezuela': 'America/Caracas', 'Ecuador': 'America/Guayaquil',
};

export const resolveTimezone = (country, stateProvince) => {
  const c = (country || '').trim();
  const s = (stateProvince || '').trim();
  if (!c) return null;
  if (STATE_TIMEZONES[c] && s && STATE_TIMEZONES[c][s]) return STATE_TIMEZONES[c][s];
  return COUNTRY_TIMEZONES[c] || null;
};

// Current (or given) local time in the zone, e.g. "9:42 PM PST". Null if zone invalid.
export const formatLocalTime = (zone, date = new Date()) => {
  if (!zone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(date);
  } catch (e) {
    return null;
  }
};
